// ─────────────────────────────────────────────
//  Location taxonomy — board section order + labels.
//
//  Locations are EDITABLE config stored on the facility document
//  (facility.locations: [{ key, label, active, custom }]). When a facility
//  has no saved list yet, we fall back to DEFAULT_LOCATIONS below.
//
//  Default board order (per spec): Main OR → Endoscopy → OB → Cath Lab →
//  Radiology. A room whose location isn't in the list still renders — it gets
//  its own section, appended after the known ones, so rooms are never hidden.
// ─────────────────────────────────────────────

// Seed list used until an admin customizes it. `key` is the stable value stored
// on each room; `label` is what users see; order here = order on the board.
export const DEFAULT_LOCATIONS = [
  { key: "main_or",   label: "Main OR",          active: true, custom: false },
  { key: "endo",      label: "Endoscopy",        active: true, custom: false },
  { key: "ob",        label: "OB (Obstetrics)",  active: true, custom: false },
  { key: "cath_lab",  label: "Cath Lab",         active: true, custom: false },
  { key: "cpru",      label: "CPRU",             active: true, custom: false },
  { key: "radiology", label: "Radiology",        active: true, custom: false },
  { key: "other",     label: "Other",            active: true, custom: false },
];

// Historical keys that should map onto a canonical one for display/sorting.
const KEY_ALIASES = { mri: "radiology", labor_delivery: "ob" };

export const canonicalKey = (key) => KEY_ALIASES[key] || key || "other";

// Turn a free-text label into a stable storage key: "Labor & Delivery" → "labor_delivery".
export function slugify(label) {
  return String(label)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "location";
}

// Fallback label for an unknown key: "cv_or" → "Cv Or".
export function prettify(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Use the facility's saved list if present, otherwise the defaults.
export function resolveLocations(facilityLocations) {
  return Array.isArray(facilityLocations) && facilityLocations.length > 0
    ? facilityLocations
    : DEFAULT_LOCATIONS;
}

// The label for a stored room key, given the resolved location list.
export function labelFor(locations, key) {
  const canon = canonicalKey(key);
  const hit = locations.find((l) => l.key === canon || l.key === key);
  return hit ? hit.label : prettify(key);
}

// Active locations only — what the room-setup and board dropdowns offer.
export const activeLocations = (locations) => locations.filter((l) => l.active !== false);

// TBD rooms ("TBD", "WC TBD", "CAR TBD"…) are an INTERNAL placeholder used to
// prevent assignment conflicts; they are never shown on the board/grid and no
// provider should be visibly assigned to them. Match the standalone token TBD.
export const isTbdRoom = (room) => /\btbd\b/i.test((room && room.name) || "");
export const isTbdRoomName = (name) => /\btbd\b/i.test(name || "");

// Natural sort so "OR 2" comes before "OR 10".
const byNameNumeric = (a, b) =>
  (a.name || "").localeCompare(b.name || "", undefined, { numeric: true });

// Group rooms into ordered sections for the board. `locations` is the resolved
// list (defines order + labels). Sections with no rooms are omitted; any room
// whose location isn't in the list still gets a section, appended at the end.
export function groupRoomsByLocation(rooms, locations) {
  const order = new Map(locations.map((l, i) => [l.key, i]));
  const buckets = new Map();

  for (const room of rooms) {
    const key = canonicalKey(room.location);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(room);
  }

  return [...buckets.keys()]
    .sort((a, b) => {
      const ra = order.has(a) ? order.get(a) : Infinity;
      const rb = order.has(b) ? order.get(b) : Infinity;
      if (ra !== rb) return ra - rb;
      return labelFor(locations, a).localeCompare(labelFor(locations, b));
    })
    .map((key) => ({
      key,
      label: labelFor(locations, key),
      rooms: buckets.get(key).sort(byNameNumeric),
    }));
}
